/**
 * Link classification for the Anchor component. Shared with the Cypress
 * link-rules helper (cypress/support/anchor-rules.ts) so the host lists that
 * decide rel tokens live in one place.
 */

export interface AnchorClassification {
  isExternal: boolean
  isMailtoOrTel: boolean
  isCitizenShipper: boolean
  /** External web link off CitizenShipper hosts: opens a new tab by default. */
  isThirdPartyWebLink: boolean
  /** Never receives rel="nofollow". */
  isTrustedDomain: boolean
}

const EXTERNAL_PREFIXES = ["http", "//", "mailto:", "tel:"]

// The main domain and help/app subdomains stay dofollow.
const DOFOLLOW_CITIZENSHIPPER_HOSTS = [
  "citizenshipper.com",
  "help.citizenshipper.com",
  "app.citizenshipper.com",
]

const TRUSTED_EXACT_HOSTS = [
  "citizenshipper.teachable.com",
  "citizenshipper.zendesk.com",
  "citizenshipper.breezy.hr",
]

const TRUSTED_HOST_SUFFIXES = [".gov", ".mil"]

// Trusted along with their subdomains.
const TRUSTED_ORG_DOMAINS = ["ipata.org", "aap.org", "avma.org"]

function isTrustedHost(hostname: string): boolean {
  const isTrustedOrg = TRUSTED_ORG_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  )

  return (
    DOFOLLOW_CITIZENSHIPPER_HOSTS.includes(hostname) ||
    TRUSTED_EXACT_HOSTS.includes(hostname) ||
    TRUSTED_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix)) ||
    isTrustedOrg
  )
}

// Used when the URL cannot be parsed: only exact dofollow origins are trusted.
function startsWithDofollowOrigin(href: string): boolean {
  return DOFOLLOW_CITIZENSHIPPER_HOSTS.some((host) => {
    const origin = `https://${host}`

    return href === origin || href.startsWith(`${origin}/`)
  })
}

export function classifyAnchorHref(href: string): AnchorClassification {
  const isExternal = EXTERNAL_PREFIXES.some((prefix) => href.startsWith(prefix))
  const isMailtoOrTel = href.startsWith("mailto:") || href.startsWith("tel:")

  if (!isExternal || isMailtoOrTel) {
    return {
      isExternal,
      isMailtoOrTel,
      isCitizenShipper: false,
      isThirdPartyWebLink: false,
      isTrustedDomain: false,
    }
  }

  // Protocol-relative URLs need a scheme for URL parsing.
  const absoluteHref = href.startsWith("//") ? `https:${href}` : href

  try {
    const { hostname } = new URL(absoluteHref)
    const isCitizenShipper =
      hostname === "citizenshipper.com" ||
      hostname.endsWith(".citizenshipper.com")

    return {
      isExternal,
      isMailtoOrTel,
      isCitizenShipper,
      isThirdPartyWebLink: !isCitizenShipper,
      isTrustedDomain: isTrustedHost(hostname),
    }
  } catch (error) {
    console.warn("Error parsing URL in Anchor component:", error)
    const isCitizenShipper = href.includes("citizenshipper.com")

    return {
      isExternal,
      isMailtoOrTel,
      isCitizenShipper,
      isThirdPartyWebLink: !isCitizenShipper,
      isTrustedDomain: startsWithDofollowOrigin(href),
    }
  }
}
